<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <LoaderBounce v-if="isSubmitting" class="py-52" />
      <template v-else>
        <template v-if="!isShowResult">
          <AreaSolutionsRequestDemoBanner />
          <AreaSolutionsRequestDemoForm />
          <PopUpFormSuccess />
          <PopUpFormWarning />
        </template>
        <AreaSolutionsRequestDemoResult v-else />
      </template>
      <NewsletterSection />
    </template>
  </article>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });
  const mainStore = useMainStore();
  const solutionsStore = useSolutionsStore();
  const { solutionData, isShowResult, isSubmitting } =
    storeToRefs(solutionsStore);
  const { provideWebTitle } = useWebsite();

  const { status: GET_COUNTRIES_STATUS } = await mainStore.USE_GET_COUNTRIES();
  const { status: GET_MEMBER_COUNTRIES_STATUS } =
    await mainStore.USE_GET_MEMBER_COUNTRIES();
  const { status: GET_SOLUTIONS_STATUS } =
    await solutionsStore.USE_GET_SOLUTIONS();

  const isLoading = computed(
    (): boolean =>
      GET_COUNTRIES_STATUS.value === 'pending' ||
      GET_MEMBER_COUNTRIES_STATUS.value === 'pending' ||
      GET_SOLUTIONS_STATUS.value === 'pending'
  );

  onBeforeMount(() => {
    isShowResult.value = false;
  });

  useSeoMeta({
    title: () =>
      t('seo-title', { solutionName: solutionData.value?.banner.title }),
    ogTitle: () =>
      provideWebTitle(
        t('seo-title', { solutionName: solutionData.value?.banner.title })
      ),
    twitterTitle: () =>
      provideWebTitle(
        t('seo-title', { solutionName: solutionData.value?.banner.title })
      ),
    description: () =>
      t('seo-description', { solutionName: solutionData.value?.banner.title }),
    ogDescription: () =>
      t('seo-description', { solutionName: solutionData.value?.banner.title }),
    twitterDescription: () =>
      t('seo-description', { solutionName: solutionData.value?.banner.title }),
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-jp": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-tw": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-kr": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-americas": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-asia-pacific": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "en-emea": {
    "seo-title": "Get Started with {solutionName}",
    "seo-description": "Request a demo to see how {solutionName} supports your business scenarios and helps drive smarter, more efficient operations."
  },
  "ja-jp": {
    "seo-title": "{solutionName} を今すぐ始める",
    "seo-description": "{solutionName} が貴社の業務シナリオをどのように支援し、よりスマートで効率的な運用を実現できるかを、今すぐデモでご確認ください。"
  },
  "zh-cn": {
    "seo-title": "立即体验 {solutionName}",
    "seo-description": "立即预约产品示范，了解 {solutionName} 如何支援您的业务场景，并协助打造更智慧、更高效的营运模式。"
  },
  "zh-tw": {
    "seo-title": "立即體驗 {solutionName}",
    "seo-description": "立即預約產品示範，了解 {solutionName} 如何支援您的業務場景，並協助打造更智慧、更高效的營運模式。"
  }
}
</i18n>
